import type React from "react";
import { useContext } from "react";

import { faPhone } from "@fortawesome/pro-regular-svg-icons";
import { Box } from "@mui/material";
import type { GridColDef } from "@mui/x-data-grid";
import { useTranslation } from "react-i18next";
import { useTheme } from "styled-components";
import invariant from "ts-invariant";

import { useGetAppointmentsV2Patient } from "api/services/appointment/hooks";
import type { AppointmentV2 } from "api/services/appointment/schemas/appointment_v2";
import { useCalendarContext } from "contexts/CalendarContext";
import { useProfileContext } from "contexts/ProfileContext";
import { getAppointmentTitle } from "routes/calendar/components/Calendar/helpers/getAppointmentTitle";
import { AppointmentDetails } from "routes/calendar/components/EventDetails";
import { EmptyResultOverlay } from "shared/molecules/EmptyResultOverlay";
import { MUIDataGrid } from "shared/molecules/MUIDataGrid";
import { CurrentPatientContext } from "utils/contexts";
import { useLocalizedDate } from "utils/date";

export const UpcomingAppointments: React.FC = () => {
  const { t } = useTranslation();
  const { format, parseISO } = useLocalizedDate();
  const { profile } = useProfileContext();
  const { patient } = useContext(CurrentPatientContext);
  const { selectedAppointment, setSelectedAppointment } = useCalendarContext();
  const theme = useTheme();

  invariant(profile);

  const { data: upcomingAppointments = [], isLoading } = useGetAppointmentsV2Patient(profile.id, patient.id);

  const columns: GridColDef[] = [
    {
      field: "date",
      headerName: t("messages.appointments.time"),
      valueGetter: (_value, row) => row.start_time,
      renderCell: ({ row }) => (
        <Box display="flex" gap="16px">
          <Box data-testid="appointment-date">{format(parseISO(row.start_time), "PP")}</Box>
          <Box display="flex" gap="2px">
            <Box fontWeight={theme.fontWeight.light}>{`${format(parseISO(row.start_time), "p")}`}</Box>-
            <Box fontWeight={theme.fontWeight.light}>{format(parseISO(row.end_time), "p")}</Box>
          </Box>
        </Box>
      ),
      flex: 1,
    },
    {
      field: "health_care_provider_service_name",
      headerName: t("messages.appointments.purpose"),
      renderCell: ({ row }) => (
        <Box fontWeight={theme.fontWeight.light}>
          {getAppointmentTitle({
            hcpServiceName: row.health_care_provider_service_name,
            purpose: row.purpose,
          })}
        </Box>
      ),
      flex: 1,
    },
  ];

  return (
    <>
      <MUIDataGrid
        columns={columns}
        rows={upcomingAppointments}
        getRowId={row => row.id}
        loading={isLoading}
        onRowClick={({ row }) => setSelectedAppointment(row as AppointmentV2)}
        getCellClassName={params => {
          if (params.row.state !== "completed") {
            return "";
          }
          return "completed-cell";
        }}
        density="compact"
        title={t("dashboard.tables.calls.title")}
        localeText={{ noRowsLabel: t("dashboard.tables.calls.empty") }}
        notifications={upcomingAppointments.filter(appointment => appointment.state !== "completed").length}
        slots={{
          noRowsOverlay: () => <EmptyResultOverlay icon={faPhone} text={t("dashboard.tables.calls.empty")} />,
        }}
      />

      {selectedAppointment && (
        <AppointmentDetails
          appointment={selectedAppointment}
          navigateToCalendar
          onClose={() => {
            setSelectedAppointment(null);
          }}
        />
      )}
    </>
  );
};
